import { h, Host } from "@stencil/core";
import { ToastMessageAnimationEnum, ToastMessageCategoryEnum } from "./toast-message.enum";
const baseClass = "ux-toast-message";
export class ToastMessage {
    constructor() {
        this.category = ToastMessageCategoryEnum.Info;
        this.lifetime = 0;
        this.message = undefined;
        this.transitionDuration = 200;
        this.transitionPhase = ToastMessageAnimationEnum.Initial;
    }
    /**
     * The underlying HTML element of the toast message component.
     */
    element;
    /**
     * An identifier for the timeout that will remove the toast message from the view. This is managed
     * internally and can be cleared if the toast is manually dismissed before the timeout.
     * @private
     */
    removeTimeout;
    /**
     * Event that is emitted when the message is removed from the stack.
     * @event uxRemove
     */
    removeEvent;
    /**
     * Public method that can be called to close the message.
     */
    async close() {
        this.handleRemove();
    }
    handlePhase() {
        window.clearTimeout(this.removeTimeout);
        switch (this.transitionPhase) {
            case ToastMessageAnimationEnum.Initial:
                window.setTimeout(() => (this.transitionPhase = ToastMessageAnimationEnum.Appear));
                break;
            case ToastMessageAnimationEnum.Appear:
                window.setTimeout(() => (this.transitionPhase = ToastMessageAnimationEnum.Idle), this.transitionDuration);
                break;
            case ToastMessageAnimationEnum.Idle:
                if (this.lifetime > 0) {
                    this.removeTimeout = window.setTimeout(this.handleRemove, this.lifetime);
                }
                break;
            case ToastMessageAnimationEnum.Disappear:
                window.setTimeout(() => {
                    if (this.element.parentElement) {
                        this.element.parentElement.removeChild(this.element);
                    }
                }, this.transitionDuration);
                break;
            default:
                break;
        }
    }
    componentWillLoad() {
        this.element.style.setProperty("--ux-toast-message-transition-duration", `${this.transitionDuration}ms`);
    }
    componentDidLoad() {
        this.handlePhase();
    }
    handleRemove = () => {
        this.removeEvent.emit();
        this.transitionPhase = ToastMessageAnimationEnum.Disappear;
    };
    renderIcon = () => {
        // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
        switch (this.category) {
            case ToastMessageCategoryEnum.Success:
                return h("ux-icon", { name: "checkmark" });
            case ToastMessageCategoryEnum.Warning:
                return h("ux-icon", { name: "triangle-warning", weight: "bold" });
            case ToastMessageCategoryEnum.Error:
                return h("ux-icon", { name: "cross" });
            case ToastMessageCategoryEnum.Info:
            default:
                return h("ux-icon", { name: "circle-info", weight: "bold" });
        }
    };
    render() {
        return (h(Host, { key: 'efda0dbe22500ee001ee5402ac668388c27fea66' }, h("div", { key: '5150fa1cb91ba38f01cd50d63daa11de8fb28b41', class: `${baseClass}__inner` }, h("div", { key: '28b27acb024d0af933078f803186c8c5b85da461', class: `${baseClass}__icon` }, this.renderIcon()), h("div", { key: '23752df604efd708e44f8d0a279cea490be5a95c', class: `${baseClass}__message` }, this.message, h("div", { key: '59e4062f5e282ad8aaa0b24505e1029a145a7cdc' }, h("slot", { key: 'a138dae4216e1e8659d08af20281c6e20d982d36' }))), this.transitionPhase === ToastMessageAnimationEnum.Idle && (h("button", { key: 'a05577b7621379f2b98605a793fce5f098cda953', class: `${baseClass}__remove`, onClick: this.handleRemove }, h("ux-icon", { key: '8a56224dad643cf4152b2813c181e6c00dba0b82', name: "cross" }))))));
    }
    static get is() { return "ux-toast-message"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["toast-message.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["toast-message.css"]
        };
    }
    static get properties() {
        return {
            "category": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "\"info\" | \"warning\" | \"error\" | \"success\" | ToastMessageCategoryEnum",
                    "resolved": "\"error\" | \"info\" | \"success\" | \"warning\" | ToastMessageCategoryEnum.Error | ToastMessageCategoryEnum.Info | ToastMessageCategoryEnum.Success | ToastMessageCategoryEnum.Warning",
                    "references": {
                        "ToastMessageCategoryEnum": {
                            "location": "import",
                            "path": "./toast-message.enum",
                            "id": "src/components/toast-message/toast-message.enum.ts::ToastMessageCategoryEnum"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [{
                            "name": "default",
                            "text": "ToastMessageCategoryEnum.Info"
                        }],
                    "text": "The category of the toast message, determining the icon and style applied. Defaults to 'Info'.\nReflects to an attribute for styling."
                },
                "attribute": "category",
                "reflect": true,
                "defaultValue": "ToastMessageCategoryEnum.Info"
            },
            "lifetime": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [{
                            "name": "default",
                            "text": "0"
                        }],
                    "text": "The lifetime of the toast message in milliseconds. If greater than 0, the toast will auto-expire\nafter this duration."
                },
                "attribute": "lifetime",
                "reflect": false,
                "defaultValue": "0"
            },
            "message": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": true,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The message text displayed to the user. This is the content of the toast notification."
                },
                "attribute": "message",
                "reflect": false
            },
            "transitionDuration": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [{
                            "name": "default",
                            "text": "200"
                        }],
                    "text": "The duration for the transition animation of the toast message in milliseconds. Controls the\nappearance and disappearance speed of the toast."
                },
                "attribute": "transition-duration",
                "reflect": false,
                "defaultValue": "200"
            },
            "transitionPhase": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "ToastMessageAnimationEnum",
                    "resolved": "ToastMessageAnimationEnum.Appear | ToastMessageAnimationEnum.Disappear | ToastMessageAnimationEnum.Idle | ToastMessageAnimationEnum.Initial",
                    "references": {
                        "ToastMessageAnimationEnum": {
                            "location": "import",
                            "path": "./toast-message.enum",
                            "id": "src/components/toast-message/toast-message.enum.ts::ToastMessageAnimationEnum"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [{
                            "name": "default",
                            "text": "ToastMessageAnimationEnum.Initial"
                        }],
                    "text": "The internal animation phase of the toast message, managing the toast's transition states.\nReflects to an attribute for CSS transitions."
                },
                "attribute": "transition-phase",
                "reflect": true,
                "defaultValue": "ToastMessageAnimationEnum.Initial"
            }
        };
    }
    static get events() {
        return [{
                "method": "removeEvent",
                "name": "uxRemove",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [{
                            "name": "event",
                            "text": "uxRemove"
                        }],
                    "text": "Event that is emitted when the message is removed from the stack."
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }];
    }
    static get methods() {
        return {
            "close": {
                "complexType": {
                    "signature": "() => Promise<void>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Public method that can be called to close the message.",
                    "tags": []
                }
            }
        };
    }
    static get elementRef() { return "element"; }
    static get watchers() {
        return [{
                "propName": "transitionPhase",
                "methodName": "handlePhase"
            }];
    }
}
//# sourceMappingURL=toast-message.js.map
